import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Heart, Loader2, X, Check, Tag, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FUNDRAISING = ["charity", "animal_rescue", "social_impact", "environment"];
const CATEGORY_OPTIONS = [
  { v: "general",        label: "General (no donate)" },
  { v: "animal_rescue",  label: "Animal rescue" },
  { v: "charity",        label: "Charity" },
  { v: "social_impact",  label: "Social impact" },
  { v: "environment",    label: "Environment" },
];
const STATUS_OPTIONS = [
  { v: "active",         label: "🟢 Active campaign" },
  { v: "goal_reached",   label: "✓ Goal reached — thank you" },
  { v: "worth_telling",  label: "📖 Story worth telling" },
];

/** Unwrap Facebook click-tracker URLs + strip fbclid/utm params. Mirrors
 *  `cleanDonationUrl` in App.js — duplicated here to keep this panel
 *  self-contained and avoid cross-file churn. */
function cleanUrl(raw) {
  const t = (raw || "").trim();
  if (!t) return "";
  try {
    let u = new URL(t);
    if (/^l\.facebook\.com$|^lm\.facebook\.com$/i.test(u.hostname) && u.pathname === "/l.php") {
      const real = u.searchParams.get("u");
      if (real) { try { u = new URL(real); } catch { /* noop */ } }
    }
    ["fbclid", "lid", "utm_id", "utm_source", "utm_medium", "utm_campaign",
     "utm_term", "utm_content", "h", "_aem"].forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch { return t; }
}

/**
 * DonationLinksPanel
 * -----------------
 * Bulk editor for donation_link across every story. Renders a pill button
 * in the Studio header which, when clicked, opens a modal listing every
 * video with:
 *   • thumbnail + title + category
 *   • inline donation_link input + Save
 *   • "Uses fallback" chip when the story is a fundraising category and has
 *     no own link (the green Donate button still lights up on the public
 *     page, pointing to DEFAULT_DONATION_URL)
 *
 * Uses the same localStorage admin-token pattern as SubmissionsPanel so the
 * studio owner only needs to authenticate once per session.
 */
export default function DonationLinksPanel({ library, reloadLibrary }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [drafts, setDrafts] = useState({}); // video_id -> draft URL
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [err, setErr] = useState("");

  // Load the public config once when the panel opens — gives us the
  // DEFAULT_DONATION_URL + list of fundraising categories.
  useEffect(() => {
    if (!open || cfg) return;
    axios.get(`${API}/config/public`, { timeout: 8000 })
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({}));
  }, [open, cfg]);

  // Rebuild the drafts map whenever we open or the library changes. We
  // never touch draft state from the library after mount so the owner can
  // paste a URL without it being overwritten by a background library
  // refresh.
  useEffect(() => {
    if (!open) return;
    setDrafts((prev) => {
      const next = { ...prev };
      (library || []).forEach((v) => {
        if (!(v.id in next)) next[v.id] = v.donation_link || "";
      });
      return next;
    });
  }, [open, library]);

  const saveOne = async (v) => {
    const draft = cleanUrl(drafts[v.id] || "");
    if (draft && !/^https?:\/\//i.test(draft)) {
      setErr(`"${v.title}" — link must start with http(s)://`);
      return;
    }
    setSavingId(v.id); setErr("");
    try {
      await axios.patch(`${API}/videos/${v.id}/impact`, { donation_link: draft || null });
      setDrafts((p) => ({ ...p, [v.id]: draft }));
      setSavedId(v.id);
      setTimeout(() => setSavedId(null), 1800);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  const clearOne = async (v) => {
    setSavingId(v.id); setErr("");
    try {
      await axios.patch(`${API}/videos/${v.id}/impact`, { donation_link: null });
      setDrafts((p) => ({ ...p, [v.id]: "" }));
      setSavedId(v.id);
      setTimeout(() => setSavedId(null), 1800);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Clear failed.");
    } finally {
      setSavingId(null);
    }
  };

  /** Flip a story's category inline so the Apact fallback starts working
   *  immediately (no need to open the separate Impact details panel). */
  const saveCategory = async (v, newCat) => {
    setSavingId(v.id); setErr("");
    try {
      await axios.patch(`${API}/videos/${v.id}/impact`, { category: newCat });
      setSavedId(v.id);
      setTimeout(() => setSavedId(null), 1800);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Category update failed.");
    } finally {
      setSavingId(null);
    }
  };

  /** Flip a story's life-stage. "goal_reached" replaces the green Donate
   *  button with a celebratory ribbon; "worth_telling" hides it entirely. */
  const saveStatus = async (v, newStatus) => {
    setSavingId(v.id); setErr("");
    try {
      await axios.patch(`${API}/videos/${v.id}/impact`, { story_status: newStatus });
      setSavedId(v.id);
      setTimeout(() => setSavedId(null), 1800);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Status update failed.");
    } finally {
      setSavingId(null);
    }
  };

  const fallbackUrl = cfg?.default_donation_url || "";
  const fundraisingCats = cfg?.fundraising_categories || FUNDRAISING;

  const items = library || [];
  const withOwn = items.filter((v) => v.donation_link).length;
  const onFallback = items.filter((v) => !v.donation_link && fundraisingCats.includes(v.category || "")).length;
  const dark = items.length - withOwn - onFallback;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Donation links"
        aria-label="Donation links"
        className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border border-emerald-500/30 bg-emerald-500/[0.08] hover:bg-emerald-500/[0.14] text-emerald-300 transition-colors"
        data-testid="donation-links-pill"
      >
        <Heart className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Donate links</span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
          onClick={() => setOpen(false)}
          data-testid="donation-links-modal"
        >
          <div
            className="panel w-full max-w-3xl mx-auto my-4 sm:my-8 p-6 md:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-[color:var(--muted)] hover:text-white transition-colors p-1.5"
              data-testid="donation-links-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-1">
              <Heart className="w-5 h-5" style={{ color: "#10b981" }} />
              <h2 className="font-display text-2xl">Donation links</h2>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-4">
              Pick a category per story — fundraising ones (animal rescue / charity / social impact / environment) auto-show a Donate button using the fallback below. Paste your own GoFundMe / JustGiving / Stripe link to override the fallback for that story. Facebook tracker URLs are auto-unwrapped on save.
            </p>

            {fallbackUrl ? (
              <div className="rounded-lg border p-3 mb-5 text-xs flex items-start gap-2"
                   style={{ background: "rgba(16, 185, 129, 0.06)", borderColor: "rgba(16, 185, 129, 0.25)" }}
                   data-testid="donation-fallback-banner">
                <Heart className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#10b981" }} />
                <div className="flex-1">
                  <div className="text-[11px] tracking-[0.22em] uppercase mb-0.5" style={{ color: "#10b981" }}>Default fallback</div>
                  <div className="text-[color:var(--muted)] leading-relaxed">
                    Any fundraising story ({fundraisingCats.map((c) => c.replace("_", " ")).join(" · ")}) without its own link auto-shows a Donate button pointing to:
                    <a href={fallbackUrl} target="_blank" rel="noopener noreferrer"
                       className="block mt-1 break-all"
                       style={{ color: "#10b981" }}>
                      {fallbackUrl}
                    </a>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3 mb-4 text-[11px] tracking-[0.18em] uppercase text-[color:var(--muted)]">
              <span data-testid="donation-summary-own">Own link: <span className="text-white">{withOwn}</span></span>
              <span className="opacity-40">·</span>
              <span data-testid="donation-summary-fallback">Fallback: <span className="text-emerald-300">{onFallback}</span></span>
              <span className="opacity-40">·</span>
              <span data-testid="donation-summary-none">No donate: <span className="text-white/50">{dark}</span></span>
            </div>

            {err && <p className="text-xs text-rose-400 mb-3" data-testid="donation-links-error">{err}</p>}

            {items.length === 0 ? (
              <div className="text-center py-12 text-[color:var(--muted)] text-sm">
                No stories yet — render one first and it'll appear here.
              </div>
            ) : (
              <div className="space-y-3" data-testid="donation-links-list">
                {items.map((v) => {
                  const isFundraising = fundraisingCats.includes(v.category || "");
                  const hasOwn = Boolean(v.donation_link);
                  const onFallbackNow = !hasOwn && isFundraising && Boolean(fallbackUrl);
                  const saving = savingId === v.id;
                  const saved = savedId === v.id;
                  const dirty = (drafts[v.id] || "") !== (v.donation_link || "");
                  return (
                    <div
                      key={v.id}
                      className="rounded-xl border border-white/5 p-4 bg-white/[0.02]"
                      data-testid={`donation-row-${v.id}`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Tag className="w-3 h-3 text-[color:var(--muted)]" />
                            <select
                              value={v.category || "general"}
                              onChange={(e) => saveCategory(v, e.target.value)}
                              disabled={saving}
                              className="input-base text-[11px] tracking-[0.16em] uppercase px-2 py-1"
                              data-testid={`donation-category-${v.id}`}
                              title="Change category — fundraising categories auto-show the Donate button"
                            >
                              {CATEGORY_OPTIONS.map((o) => (
                                <option key={o.v} value={o.v}>{o.label}</option>
                              ))}
                            </select>
                            <select
                              value={v.story_status || "active"}
                              onChange={(e) => saveStatus(v, e.target.value)}
                              disabled={saving}
                              className="input-base text-[11px] px-2 py-1"
                              data-testid={`donation-status-${v.id}`}
                              title="Story life-stage. Goal reached replaces Donate with a thank-you ribbon."
                            >
                              {STATUS_OPTIONS.map((o) => (
                                <option key={o.v} value={o.v}>{o.label}</option>
                              ))}
                            </select>
                            {hasOwn ? (
                              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                    data-testid={`donation-chip-${v.id}-own`}>
                                Own link
                              </span>
                            ) : onFallbackNow ? (
                              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                    data-testid={`donation-chip-${v.id}-fallback`}>
                                Using fallback
                              </span>
                            ) : isFundraising ? (
                              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">
                                Hidden
                              </span>
                            ) : (
                              <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">
                                Not fundraising
                              </span>
                            )}
                          </div>
                          <div className="font-display text-base leading-tight truncate" data-testid={`donation-title-${v.id}`}>
                            {v.title || "Untitled"}
                          </div>
                          {v.slug && (
                            <div className="flex items-center gap-3 mt-0.5">
                              <a href={`/s/${v.slug}`} target="_blank" rel="noopener noreferrer"
                                 className="text-[11px] text-[color:var(--muted)] hover:text-white inline-flex items-center gap-1"
                                 data-testid={`donation-preview-${v.id}`}
                                 title="Open the public story page in a new tab">
                                <Eye className="w-3 h-3" /> Preview
                              </a>
                              <span className="text-[11px] text-[color:var(--muted)] opacity-60 truncate">/s/{v.slug}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <input
                          type="url"
                          placeholder={onFallbackNow ? fallbackUrl : "https://gofundme.com/f/…"}
                          value={drafts[v.id] ?? ""}
                          onChange={(e) => setDrafts((p) => ({ ...p, [v.id]: e.target.value }))}
                          onBlur={() => {
                            const c = cleanUrl(drafts[v.id] || "");
                            if (c !== (drafts[v.id] || "")) setDrafts((p) => ({ ...p, [v.id]: c }));
                          }}
                          className="input-base flex-1 min-w-[240px] px-3 py-2 text-sm"
                          data-testid={`donation-input-${v.id}`}
                        />
                        <button
                          onClick={() => saveOne(v)}
                          disabled={saving || !dirty}
                          className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 whitespace-nowrap"
                          style={{ background: "#10b981", color: "#04150d" }}
                          data-testid={`donation-save-${v.id}`}
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : <Heart className="w-3 h-3" />}
                          {saved ? "Saved" : "Save"}
                        </button>
                        {hasOwn && (
                          <button
                            onClick={() => clearOne(v)}
                            disabled={saving}
                            className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5 whitespace-nowrap"
                            title="Remove own link — revert to fallback (or no button) based on category"
                            data-testid={`donation-clear-${v.id}`}
                          >
                            <X className="w-3 h-3" /> Clear
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
